import { useLDClient } from 'launchdarkly-vue-client-sdk';
import { watch } from 'vue';
import { useAuthStore } from '@/stores/auth-store.ts';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Composable for syncing user context with authentication state across analytics and feature flagging providers.
 *
 * When the user authenticates, this updates LaunchDarkly and Mixpanel with user-specific context
 * containing their profile information for feature flag targeting and analytics.
 *
 * When the user logs out, it resets to an anonymous context.
 */
export function useUserContext() {
   const authStore = useAuthStore();
   const ldClient = useLDClient();

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Identify user with LaunchDarkly
    */
   const identifyWithLD = async (profile: NonNullable<typeof authStore.userProfile>) => {
      await ldClient.identify({
         kind: 'user',
         key: profile.user_id,
         email: profile.email,
         name: `${profile.firstname} ${profile.lastname}`.trim() || undefined,
         firstName: profile.firstname || undefined,
         lastName: profile.lastname || undefined,
         // Custom attributes for targeting
         organization: profile.organization,
         orgId: profile.org_id,
         userType: profile.user_type,
         orgUserRole: profile.org_user_role,
         city: profile.city || undefined,
         state: profile.state || undefined,
         country: profile.country || undefined,
         premium: profile.user_type === 'premium',
      });
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Identify user with Mixpanel
    */
   const identifyWithMixpanel = (profile: NonNullable<typeof authStore.userProfile>) => {
      if (!('mixpanel' in window)) {
         return;
      }

      const mp = window.mixpanel as { identify: (userId: string) => void; people: { set: (props: Record<string, unknown>) => void } };
      mp.identify(profile.user_id);
      mp.people.set({
         $name: `${profile.firstname} ${profile.lastname}`.trim() || undefined,
         $email: profile.email,
         organization: profile.organization,
         user_type: profile.user_type,
         org_user_role: profile.org_user_role,
         plan: profile.user_type === 'premium' ? 'Premium' : 'Standard',
      });
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Update user context across all providers when user authenticates
    */
   const identifyUser = async () => {
      const profile = authStore.userProfile;

      if (!profile) {
         console.warn('[UserContext] Cannot identify user: no profile available');
         return;
      }

      try {
         await identifyWithLD(profile);
      }
      catch (e) {
         console.warn('[UserContext] Failed to identify with LaunchDarkly:', e);
      }

      try {
         identifyWithMixpanel(profile);
      }
      catch (e) {
         console.warn('[UserContext] Failed to identify with Mixpanel:', e);
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Reset user context to anonymous across all providers
    */
   const resetToAnonymous = async () => {
      await ldClient.identify({
         kind: 'user',
         anonymous: true,
         key: 'anonymous-user',
         premium: false,
      });

      if ('mixpanel' in window) {
         const mp = window.mixpanel as { reset: () => void };
         mp.reset();
      }
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Watch for changes in user profile and update context across all providers.
    * This should be called once during app initialization.
    */
   const watchAuthState = () => {
      // Watch for userProfile changes (set after authentication + profile fetch)
      watch(
         () => authStore.userProfile,
         async (newProfile, oldProfile) => {
            if (newProfile && !oldProfile) {
               // User just logged in and profile was fetched
               await identifyUser();
            }
            else if (!newProfile && oldProfile) {
               // User just logged out
               await resetToAnonymous();
            }
         },
         { immediate: true },
      );
   };

   /* ------------------------------------------------------------------------------------------- */

   return {
      identifyUser,
      resetToAnonymous,
      watchAuthState,
   };
}
